// Copyright (c) 2022 by Move Closer

import { Component, Prop, Vue } from 'vue-property-decorator'
import { dayNamesRegistry } from '../contracts/days'

/**
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
export interface IDeliveryDate {
  deliveryFrom: Date | null
  deliveryTo: Date | null
  maxOrderHours: number
  get hasTimer (): boolean
  get isDayHoliday (): boolean
  getDeliveryWeekday (): string | null
  getDeliveryWeekdayLabel (): string
  getDeliveryDate (): { from: Date; to: Date }
  getWeekday (date: Date, excludeWeeknames?: boolean): string | null
}

/**
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
@Component<DeliveryDateMixin>({
  created () {
    const newLimit = parseInt(this.limitHours)
    if (newLimit) {
      this.maxOrderHours = newLimit
    }

    const { from, to } = this.getDeliveryDate()
    this.deliveryFrom = from
    this.deliveryTo = to
  }
})
export class DeliveryDateMixin extends Vue implements IDeliveryDate {
  @Prop({ type: String, required: false, default: '17' })
  public limitHours!: string

  // todo: values set in cms, retrieved in init call
  public MIN_WORKING_DAYS = 1
  public MAX_WORKING_DAYS = 2
  public maxOrderHours: number = 15

  public deliveryFrom: Date | null = null
  public deliveryTo: Date | null = null

  private readonly today = new Date()

  protected staticHolidays = [
    [0, 1], // Nowy Rok
    [0, 6], // Święto Trzech Króli
    [4, 1], // 1 Maja
    [4, 3], // 3 Maja
    [7, 15], // WNP
    [10, 1], // Wszystkich Świętych
    [10, 11], // Dzień Niepodległości
    [11, 25], // Boże Narodzenie
    [11, 26] // Boże Narodzenie (drugi dzień)
  ]

  public get isDayHoliday (): boolean {
    return this.isHoliday(this.today)
  }

  public getDeliveryDate (): { from: Date; to: Date } {
    const from = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate())
    const to = new Date(from)
    let day = from.getDate()
    // ordering after MAX_ORDER_HOUR means we need one more working day
    let i = this.today.getHours() < this.maxOrderHours ? 1 : 0

    // original beginning date preventing from occurring bugs over change of the months
    // e.g. `from: 31.07.2023` results with inadequate `to` date
    const originalFrom = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate())

    while (i <= this.MIN_WORKING_DAYS) {
      const _from = new Date(from)
      _from.setDate(++day)
      if (!this.isHoliday(_from)) {
        i++
      }
    }
    from.setDate(day)

    while (i <= this.MAX_WORKING_DAYS) {
      const _to = new Date(originalFrom)
      _to.setDate(++day)
      if (!this.isHoliday(_to)) {
        i++
      }
    }
    to.setDate(day)

    return { from, to }
  }

  public getDeliveryWeekday (): string | null {
    if (!this.deliveryFrom) {
      return null
    }

    return this.getWeekday(this.deliveryFrom, true)
  }

  public getDeliveryWeekdayLabel (): string {
    const deliveryDay = this.getDeliveryWeekday()
    if (!deliveryDay) {
      return ''
    }

    return this.$t(`front.shared.mixins.deliveryDateMixin.days.${deliveryDay}`).toString()
  }

  public getWeekday (date: Date, excludeWeeknames?: boolean): string | null {
    const ONE_DAY = 24 * 60 * 60 * 1000
    const todayMidnight = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate())
    const diff = date.getTime() - todayMidnight.getTime()

    if (diff < ONE_DAY) {
      return 'today'
    } else if (diff < 2 * ONE_DAY) {
      return 'tomorrow'
    } else if (diff < 3 * ONE_DAY) {
      return 'dayAfterTomorrow'
    } else {
      return dayNamesRegistry[date.getDay()]
    }
  }

  public get hasTimer () {
    return this.today.getHours() < this.maxOrderHours
  }

  /**
   * Oblicza dzień obchodzenia Wielkanocy w podanym roku wg algorytmu Gaussa
   */
  protected getEasterDay (year: number) {
    if (year >= 2500) {
      throw new Error('Nieobsługiwany zakres dat do obliczenia Wielkanocy')
    }

    const a = year % 19
    const b = year % 4
    const c = year % 7
    const factors = this.getEasterFactors(year)
    let d = (a * 19 + factors.factorA) % 30
    const e = (2 * b + 4 * c + 6 * d + factors.factorB) % 7

    if ((d === 29 || d === 28) && e === 6) {
      d -= 7
    }
    return new Date(year, 2, 22 + d + e)
  }

  protected getEasterFactors (year: number) {
    if (year <= 1582) {
      return { factorA: 15, factorB: 6 }
    } else if (year >= 1583 && year <= 1699) {
      return { factorA: 22, factorB: 2 }
    } else if (year >= 1700 && year <= 1799) {
      return { factorA: 23, factorB: 3 }
    } else if (year >= 1800 && year <= 1899) {
      return { factorA: 23, factorB: 4 }
    } else if (year >= 1900 && year <= 2099) {
      return { factorA: 24, factorB: 5 }
    } else if (year >= 2100 && year <= 2199) {
      return { factorA: 24, factorB: 6 }
    } else if (year >= 2200 && year <= 2299) {
      return { factorA: 25, factorB: 0 }
    } else if (year >= 2300 && year <= 2399) {
      return { factorA: 26, factorB: 1 }
    } else if (year >= 2400 && year <= 2499) {
      return { factorA: 25, factorB: 1 }
    } else {
      throw new Error('Nieobsługiwany zakres dat do obliczenia Wielkanocy')
    }
  }

  protected movingHolidays (year: number) {
    const easterDate = this.getEasterDay(year)
    const easterMonth = easterDate.getMonth()
    const easterDay = easterDate.getDay()
    const easterMonday = new Date(year, easterMonth, easterDay + 1)
    const greenDays = new Date(year, easterMonth, easterDay + 49)
    const corpusChristi = new Date(year, easterMonth, easterDay + 60)

    return [
      [easterMonday.getMonth(), easterMonday.getDate()], // Wielkanoc
      [greenDays.getMonth(), greenDays.getDate()], // Zielone Świątki
      [corpusChristi.getMonth(), corpusChristi.getDate()] // Boże Ciało
    ]
  }

  protected isHoliday (date: Date) {
    const weekday = date.getDay()

    if (weekday === 6 || weekday === 0) {
      return true
    }

    const month = date.getMonth()
    const day = date.getDate()

    if (typeof this.staticHolidays.find((holiday) => holiday[0] === month && holiday[1] === day) !== 'undefined') {
      return true
    }

    const year = date.getFullYear()
    return this.movingHolidays(year).includes([month, day])
  }
}
